import queryString from "query-string";
import i18next from "src/i18n/config";
import RouteService from "src/services/routeService";
import { invalidateOnSuccess, transformErrorResponse } from "src/store/helpers";
import { ApiResponse, BasicApiResponse, PaginatedResponse } from "src/types";

import { Build, Product } from "@dashboard/products/types";
import { rolloutsApi } from "@dashboard/rollouts/api";
import { BaseQueryFn, EndpointBuilder } from "@reduxjs/toolkit/query";

import { BuildConfig } from "./apiClient";
import { serializeBuild, serializeProduct } from "./helpers";

type ProductsBuilder = EndpointBuilder<BaseQueryFn, "Products" | "Builds" | "Variables" | "ChangeLogs", "productsApi">;

type AddVariablePayload = {
    productId: number;
    variableData: {
        key: string;
        value: string;
        description?: string;
    };
};
function addProductVariable(builder: ProductsBuilder) {
    return builder.mutation<API.ProductVariable, AddVariablePayload>({
        invalidatesTags: invalidateOnSuccess("Variables"),
        transformErrorResponse: (err) =>
            transformErrorResponse(err, i18next.t("common:error.products.failedToAddProductVariable")),
        transformResponse: (response: ApiResponse<API.ProductVariable>) => response.model,
        query: async (payload) => {
            const formData = new FormData();
            formData.append("key", payload.variableData.key);
            formData.append("value", payload.variableData.value);
            if (payload.variableData.description) {
                formData.append("description", payload.variableData.description);
            }
            const endpoint = await RouteService.getProductRoute();
            return {
                url: `${endpoint}products/${payload.productId}/variables`,
                method: "POST",
                data: formData,
                headers: { "Content-Type": "multipart/form-data" },
            };
        },
    });
}

function cancelBuild(builder: ProductsBuilder) {
    return builder.mutation<Build["buildId"], Build["buildId"]>({
        invalidatesTags: invalidateOnSuccess("Builds"),
        transformErrorResponse: (err) => transformErrorResponse(err, i18next.t("common:error.products.failedToCancelBuild")),
        query: async (payload) => {
            const endpoint = await RouteService.getProductRoute();
            return {
                url: `${endpoint}products/build`,
                method: "DELETE",
                headers: { "content-type": "application/x-www-form-urlencoded" },
                data: queryString.stringify({ buildId: payload }),
            };
        },
    });
}

type CreateBuildPayload = {
    productId: number;
    config?: Partial<BuildConfig>;
};
function createBuild(builder: ProductsBuilder) {
    return builder.mutation<Build, CreateBuildPayload>({
        invalidatesTags: invalidateOnSuccess("Builds", "Products", "ChangeLogs"),
        transformErrorResponse: (err) => transformErrorResponse(err, i18next.t("common:error.products.failedToCreateBuild")),
        transformResponse: (response: ApiResponse<API.Build>) => serializeBuild(response.model),
        query: async ({ productId, config = {} }) => {
            const endpoint = await RouteService.getProductRoute();
            return {
                url: `${endpoint}products/${productId}/build`,
                method: "POST",
                data: JSON.stringify(config),
                headers: { "content-type": "application/json" },
            };
        },
    });
}

function createProduct(builder: ProductsBuilder) {
    return builder.mutation<Product, Product>({
        invalidatesTags: invalidateOnSuccess("Products"),
        transformErrorResponse: (err) => transformErrorResponse(err, i18next.t("common:error.products.failedToCreateProduct")),
        transformResponse: (response: ApiResponse<API.Product>) => serializeProduct([response.model])[0],
        query: async (payload) => {
            const endpoint = await RouteService.getProductRoute();
            return {
                url: endpoint + "products",
                method: "POST",
                data: JSON.stringify(payload),
                headers: { "content-type": "application/json" },
            };
        },
    });
}

function deleteBuild(builder: ProductsBuilder) {
    return builder.mutation<Build["buildId"], { productId: Build["productId"]; buildId: Build["buildId"] }>({
        invalidatesTags: invalidateOnSuccess("Builds", "Products"),
        transformErrorResponse: (err) => transformErrorResponse(err, i18next.t("common:error.products.failedToDeleteBuild")),
        query: async (payload) => {
            const endpoint = await RouteService.getProductRoute();
            return {
                url: `${endpoint}products/${payload.productId}/builds/${payload.buildId}`,
                method: "DELETE",
            };
        },
        onQueryStarted: async (_payload, { queryFulfilled, dispatch }) => {
            try {
                await queryFulfilled;
                dispatch(rolloutsApi.util.invalidateTags(["OtaFiles"]));
            } catch {
                // delete failed — nothing to invalidate
            }
        },
    });
}

function deleteProduct(builder: ProductsBuilder) {
    return builder.mutation<string, number>({
        invalidatesTags: invalidateOnSuccess("Products"),
        transformErrorResponse: (err) => transformErrorResponse(err, i18next.t("common:error.products.failedToDeleteProduct")),
        transformResponse: (response: BasicApiResponse) => response.message,
        query: async (payload) => {
            const endpoint = await RouteService.getProductRoute();
            return {
                url: `${endpoint}products/${payload}`,
                method: "DELETE",
            };
        },
    });
}

type DeleteProductChangesPayload = {
    productId: number;
    version: string;
};
function deleteProductChangeLog(builder: ProductsBuilder) {
    return builder.mutation<API.Product["changes"], DeleteProductChangesPayload>({
        invalidatesTags: invalidateOnSuccess("ChangeLogs"),
        transformErrorResponse: (err) =>
            transformErrorResponse(err, i18next.t("common:error.products.failedToDeleteProductChangeLog")),
        transformResponse: (response: ApiResponse<API.Product["changes"]>) => response.model,
        query: async (payload) => {
            const formData = new FormData();
            formData.append("version", payload.version);

            const endpoint = await RouteService.getProductRoute();
            return {
                url: `${endpoint}products/${payload.productId}/changes`,
                method: "DELETE",
                headers: { "content-type": "multipart/form-data" },
                data: formData,
            };
        },
    });
}

function deleteProductVariable(builder: ProductsBuilder) {
    return builder.mutation<string, { productId: number; variableKey: string }>({
        invalidatesTags: invalidateOnSuccess("Variables"),
        transformErrorResponse: (err) =>
            transformErrorResponse(err, i18next.t("common:error.products.failedToDeleteProductVariable")),
        transformResponse: (response: BasicApiResponse) => response.message,
        query: async ({ productId, variableKey }) => {
            const endpoint = await RouteService.getProductRoute();
            return {
                url: `${endpoint}products/${productId}/variables/${variableKey}`,
                method: "DELETE",
            };
        },
    });
}

function fetchAllProducts(builder: ProductsBuilder) {
    return builder.query<API.Product[], void>({
        providesTags: ["Products"],
        transformErrorResponse: (err) => transformErrorResponse(err, i18next.t("common:error.products.failedToFetchProducts")),
        transformResponse: (response: ApiResponse<API.Product[]>) => response.model,
        query: async () => {
            const endpoint = await RouteService.getProductRoute();
            return { url: endpoint + "products/all" };
        },
    });
}

function fetchProductBuilds(builder: ProductsBuilder) {
    return builder.query<Build[], number>({
        providesTags: ["Builds"],
        transformErrorResponse: (err) => transformErrorResponse(err, i18next.t("common:error.products.failedToFetchBuilds")),
        transformResponse: (response: ApiResponse<API.Build[]>) => response.model.map(serializeBuild),
        query: async (productId) => {
            const endpoint = await RouteService.getProductRoute();
            return { url: `${endpoint}products/${productId}/builds` };
        },
    });
}

function fetchBuildsForProducts(builder: ProductsBuilder) {
    return builder.query<Build[], number[]>({
        providesTags: ["Builds"],
        queryFn: async (productIds, _api, _extraOptions, baseQuery) => {
            const endpoint = await RouteService.getProductRoute();
            const results = await Promise.all(
                productIds.map((productId) => baseQuery({ url: `${endpoint}products/${productId}/builds` })),
            );
            const failure = results.find((result) => result.error);
            if (failure?.error) {
                return {
                    error: transformErrorResponse(failure.error, i18next.t("common:error.products.failedToFetchBuilds")),
                };
            }
            const builds = results.flatMap((result) => (result.data as ApiResponse<API.Build[]>).model.map(serializeBuild));
            return { data: builds };
        },
    });
}

function fetchProductChangeLogs(builder: ProductsBuilder) {
    return builder.query<API.Product["changes"], number | undefined>({
        providesTags: ["ChangeLogs"],
        transformErrorResponse: (err) =>
            transformErrorResponse(err, i18next.t("common:error.products.failedToFetchProductChangeLogs")),
        transformResponse: (response: ApiResponse<API.Product["changes"]>) => response.model,
        query: async (id) => {
            const endpoint = await RouteService.getProductRoute();
            return { url: `${endpoint}products/${id}/changes` };
        },
    });
}

function fetchProductById(builder: ProductsBuilder) {
    return builder.query<Product, number>({
        providesTags: ["Products"],
        transformErrorResponse: (err) => transformErrorResponse(err, i18next.t("common:error.products.failedToFetchProduct")),
        transformResponse: (response: ApiResponse<API.Product>) => serializeProduct([response.model])[0],
        query: async (id) => {
            const endpoint = await RouteService.getProductRoute();
            return { url: `${endpoint}products/${id}` };
        },
    });
}

type FetchProductsPayload = {
    workspaceId: number;
    isTerminated?: boolean;
};
function fetchProducts(builder: ProductsBuilder) {
    return builder.query<Product[], FetchProductsPayload>({
        providesTags: ["Products"],
        queryFn: async (payload, _api, _extraOptions, baseQuery) => {
            if (payload.isTerminated) {
                return { data: [] };
            }
            const endpoint = await RouteService.getProductRoute();
            const result = await baseQuery({
                url: endpoint + "products",
                params: { workspaceId: payload.workspaceId },
            });
            if (result.error) {
                return {
                    error: transformErrorResponse(result.error, i18next.t("common:error.products.failedToFetchProducts")),
                };
            }
            return { data: serializeProduct((result.data as ApiResponse<API.Product[]>).model) };
        },
    });
}

function fetchProductVariables(builder: ProductsBuilder) {
    return builder.query<API.ProductVariable[], number>({
        providesTags: ["Variables"],
        transformErrorResponse: (err) =>
            transformErrorResponse(err, i18next.t("common:error.products.failedToFetchProductVariables")),
        transformResponse: (response: PaginatedResponse<API.ProductVariable>) => response.list,
        query: async (productId) => {
            const endpoint = await RouteService.getProductRoute();
            return { url: `${endpoint}products/${productId}/variables` };
        },
    });
}

function fetchVisibleProducts(builder: ProductsBuilder) {
    return builder.query<API.VisibleProduct[], void>({
        providesTags: ["Products"],
        transformErrorResponse: (err) =>
            transformErrorResponse(err, i18next.t("common:error.products.failedToFetchVisibleProducts")),
        transformResponse: (response: PaginatedResponse<API.VisibleProduct>) => response.list,
        query: async () => {
            const endpoint = await RouteService.getSettingsRoute();
            return { url: endpoint + "products?visibility=PublicDeviceRegistration" };
        },
    });
}

type ImportProductBuildPayload = {
    productId: number;
    product: Product;
    build: Build;
};
type ImportProductBuildResponse = {
    product: Product;
    build: Build;
    uploadURLs: {
        id: number;
        uploadURL: string;
    }[];
};
function importProductBuild(builder: ProductsBuilder) {
    return builder.mutation<ImportProductBuildResponse, ImportProductBuildPayload>({
        invalidatesTags: invalidateOnSuccess("Builds", "Products"),
        onQueryStarted: async (_payload, { queryFulfilled, dispatch }) => {
            try {
                await queryFulfilled;
                // A build's channel is what the rollouts page filters its OTA list by, and that list lives in a
                // different api slice, so invalidating "Builds" here does not reach it.
                dispatch(rolloutsApi.util.invalidateTags(["OtaFiles"]));
            } catch {
                // the write failed — nothing to invalidate
            }
        },
        transformErrorResponse: (err) =>
            transformErrorResponse(err, i18next.t("common:error.products.failedToImportProductBuild")),
        transformResponse: (response: ApiResponse<ImportProductBuildResponse>) => response.model,
        query: async ({ productId, ...payload }) => {
            const endpoint = await RouteService.getProductRoute();
            return {
                url: `${endpoint}products/${productId}/builds`,
                method: "PUT",
                data: payload,
            };
        },
    });
}

type UpdateBuildChannelPayload = {
    productId: number;
    buildId: number;
    channel: Build["channel"];
};
function updateBuildChannel(builder: ProductsBuilder) {
    return builder.mutation<UpdateBuildChannelPayload, UpdateBuildChannelPayload>({
        invalidatesTags: invalidateOnSuccess("Builds"),
        onQueryStarted: async (_payload, { queryFulfilled, dispatch }) => {
            try {
                await queryFulfilled;
                // A build's channel is what the rollouts page filters its OTA list by, and that list lives in a
                // different api slice, so invalidating "Builds" here does not reach it.
                dispatch(rolloutsApi.util.invalidateTags(["OtaFiles"]));
            } catch {
                // the write failed — nothing to invalidate
            }
        },
        transformErrorResponse: (err) =>
            transformErrorResponse(err, i18next.t("common:error.products.failedToUpdateBuildChannel")),
        query: async (payload) => {
            const endpoint = await RouteService.getProductRoute();
            return {
                url: `${endpoint}products/${payload.productId}/builds/${payload.buildId}/channel`,
                method: "PATCH",
                data: payload.channel,
                headers: { "content-type": "application/json" },
            };
        },
    });
}

type UpdateBuildMetadataPayload = {
    productId: number;
    buildId: number;
    metadata: { tags: API.Tag[] };
};
function updateBuildMetadata(builder: ProductsBuilder) {
    return builder.mutation<UpdateBuildMetadataPayload, UpdateBuildMetadataPayload>({
        invalidatesTags: invalidateOnSuccess("Builds"),
        transformErrorResponse: (err) =>
            transformErrorResponse(err, i18next.t("common:error.products.failedToUpdateBuildMetadata")),
        query: async (payload) => {
            const endpoint = await RouteService.getProductRoute();
            return {
                url: `${endpoint}products/${payload.productId}/builds/${payload.buildId}/metadata`,
                method: "PATCH",
                data: payload.metadata,
            };
        },
    });
}

type UpdateBuildRolloutPayload = {
    productId: number;
    buildId: number;
    rolloutPercentage: Build["rolloutPercentage"];
};
function updateBuildRollout(builder: ProductsBuilder) {
    return builder.mutation<UpdateBuildRolloutPayload, UpdateBuildRolloutPayload>({
        invalidatesTags: invalidateOnSuccess("Builds"),
        transformErrorResponse: (err) =>
            transformErrorResponse(err, i18next.t("common:error.products.failedToUpdateBuildRollout")),
        query: async (payload) => {
            const endpoint = await RouteService.getProductRoute();
            return {
                url: `${endpoint}products/${payload.productId}/builds/${payload.buildId}/rollout`,
                method: "PATCH",
                data: payload.rolloutPercentage,
                headers: { "content-type": "application/json" },
            };
        },
    });
}

type UpdateFileCompatibilityPayload = {
    fileId: number;
    compatibility: string;
};
function updateFileFullOtaCompatibility(builder: ProductsBuilder) {
    return builder.mutation<UpdateFileCompatibilityPayload, UpdateFileCompatibilityPayload>({
        invalidatesTags: invalidateOnSuccess("Builds"),
        transformErrorResponse: (err) =>
            transformErrorResponse(err, i18next.t("common:error.products.failedToUpdateFileFullOtaCompatibility")),
        query: async (payload) => {
            const endpoint = await RouteService.getProductRoute();
            return {
                url: `${endpoint}files/${payload.fileId}/compatibility`,
                method: "PATCH",
                data: { compatibility: payload.compatibility },
                headers: { "content-type": "application/json" },
            };
        },
    });
}

function updateProduct(builder: ProductsBuilder) {
    return builder.mutation<Product, Partial<API.Product>>({
        invalidatesTags: invalidateOnSuccess("Products"),
        transformErrorResponse: (err) => transformErrorResponse(err, i18next.t("common:error.products.failedToUpdateProduct")),
        transformResponse: (response: ApiResponse<API.Product>) => serializeProduct([response.model])[0],
        query: async (payload) => {
            const endpoint = await RouteService.getProductRoute();
            return {
                url: endpoint + "products",
                method: "PATCH",
                data: JSON.stringify(payload),
                headers: { "content-type": "application/json" },
            };
        },
    });
}

function updateProductAndroidVersion(builder: ProductsBuilder) {
    return builder.mutation<API.Product, { productId: number; newVersion: string }>({
        invalidatesTags: invalidateOnSuccess("Products"),
        transformErrorResponse: (err) =>
            transformErrorResponse(err, i18next.t("common:error.products.failedToUpdateProductAndroidVersion")),
        transformResponse: (response: ApiResponse<API.Product>) => response.model,
        query: async ({ productId, newVersion }) => {
            const endpoint = await RouteService.getProductRoute();
            return {
                url: `${endpoint}products/${productId}/version`,
                method: "PATCH",
                data: queryString.stringify({ newVersion }),
            };
        },
    });
}

type BuildChangesPayload = {
    productId: number;
    version: string;
    changes: string[];
};
function updateProductChangeLog(builder: ProductsBuilder) {
    return builder.mutation<API.Product["changes"], BuildChangesPayload>({
        invalidatesTags: invalidateOnSuccess("ChangeLogs"),
        transformErrorResponse: (err) =>
            transformErrorResponse(err, i18next.t("common:error.products.failedToUpdateProductChangeLog")),
        transformResponse: (response: ApiResponse<API.Product["changes"]>) => response.model,
        query: async (payload) => {
            const formData = new FormData();
            formData.append("version", payload.version);
            payload.changes.forEach((change) => formData.append("changes", change));

            const endpoint = await RouteService.getProductRoute();
            return {
                url: `${endpoint}products/${payload.productId}/changes`,
                method: "PATCH",
                data: formData,
                headers: { "content-type": "multipart/form-data" },
            };
        },
    });
}

function updateProductCodename(builder: ProductsBuilder) {
    return builder.mutation<API.Product, { productId: number; newCodename: string }>({
        invalidatesTags: invalidateOnSuccess("Products"),
        transformErrorResponse: (err) =>
            transformErrorResponse(err, i18next.t("common:error.products.failedToUpdateProductCodename")),
        transformResponse: (response: ApiResponse<API.Product>) => response.model,
        query: async ({ productId, newCodename }) => {
            const endpoint = await RouteService.getProductRoute();
            return {
                url: `${endpoint}products/${productId}/codename`,
                method: "PATCH",
                data: queryString.stringify({ newCodename }),
            };
        },
    });
}

function updateProductVisibility(builder: ProductsBuilder) {
    return builder.mutation<API.Product, { productId: number; visibility: string[] }>({
        invalidatesTags: invalidateOnSuccess("Products"),
        transformErrorResponse: (err) =>
            transformErrorResponse(err, i18next.t("common:error.products.failedToUpdateProductProperties")),
        transformResponse: (response: ApiResponse<API.Product>) => response.model,
        query: async ({ productId, visibility }) => {
            const endpoint = await RouteService.getProductRoute();
            return {
                url: `${endpoint}products/${productId}/visibility`,
                method: "PATCH",
                data: JSON.stringify({ visibility }),
                headers: { "content-type": "application/json" },
            };
        },
    });
}

export default {
    addProductVariable,
    cancelBuild,
    createBuild,
    createProduct,
    deleteBuild,
    deleteProduct,
    deleteProductChangeLog,
    deleteProductVariable,
    fetchAllProducts,
    fetchBuildsForProducts,
    fetchProductBuilds,
    fetchProductChangeLogs,
    fetchProductById,
    fetchProducts,
    fetchProductVariables,
    fetchVisibleProducts,
    importProductBuild,
    updateBuildChannel,
    updateBuildMetadata,
    updateBuildRollout,
    updateFileFullOtaCompatibility,
    updateProduct,
    updateProductAndroidVersion,
    updateProductChangeLog,
    updateProductCodename,
    updateProductVisibility,
};
